import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { useSSE } from "../../hooks/useSSE";
import {
  PROPS,
  applyPresence,
  clock,
  lastVisitLine,
  liveCounts,
  liveHeadline,
  liveLook,
  secondsSince,
  sortLive,
  sourceView,
  stageChip,
  todayLine,
  trail,
  whereLabel,
} from "./analyticsModel.mjs";
import { Country, PropDot, SourceCell, StageChip } from "./parts";

const RESYNC_MS = 60 * 1000;
const FADE_MS = 400;

/*
 * LIVE NOW: who is on remotion.io, a landing page or the booking page this
 * second (src/analytics/presence.js). The list is read once, then kept by the
 * SSE "presence" events; a read every minute catches anything a dropped stream
 * missed and refreshes the quiet line. Rows appear, move and fade on their own;
 * people the CRM knows sort to the top. Their names open the side panel.
 */
export default function LivePanel({ onOpen, onLive }) {
  const [list, setList] = useState(null);
  const [recent, setRecent] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const fading = useRef(new Map());

  const load = useCallback(async () => {
    try {
      const data = await api("/analytics/live");
      setList(sortLive(data.visitors || []));
      setRecent(data.recent || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, RESYNC_MS);
    return () => clearInterval(t);
  }, [load]);

  // The timers run off one clock for the whole panel.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useSSE("presence", (ev) => {
    if (!ev || !ev.entry) return;
    if (ev.type === "leave") {
      // Fade, then go.
      const id = ev.entry.id;
      setList((l) => (l || []).map((e) => (e.id === id ? { ...e, leaving: true } : e)));
      clearTimeout(fading.current.get(id));
      fading.current.set(
        id,
        setTimeout(() => {
          fading.current.delete(id);
          setList((l) => (l || []).filter((e) => e.id !== id));
        }, FADE_MS)
      );
      return;
    }
    setList((l) => applyPresence(l || [], ev));
  });

  useEffect(() => {
    const timers = fading.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  // The side panel wants to know whether its person is on now.
  useEffect(() => {
    if (onLive) onLive(list || []);
  }, [list, onLive]);

  const rows = (list || []).filter(Boolean);
  const shown = rows.filter((e) => !e.leaving);
  const n = liveCounts(shown);
  const quiet = list !== null && shown.length === 0 && rows.length === 0;

  return (
    <section className="an-card an-live" aria-label="Live now">
      <div className="an-live__head">
        <div className="an-live__title">
          <span aria-hidden="true" className={`an-live__dot${shown.length ? " is-on" : ""}`} />
          <h2>Live now</h2>
          <span className="an-muted">{list === null ? "Listening…" : liveHeadline(shown.length)}</span>
        </div>
        <div className="an-live__counts">
          {Object.keys(PROPS).map((k) => (
            <span key={k} className={`an-countchip${n[k] ? "" : " is-zero"}`}>
              <PropDot prop={k} />
              {PROPS[k].label}
              <b>{n[k]}</b>
            </span>
          ))}
        </div>
      </div>

      {error && <p className="an-live__error">Could not read who is on: {error}</p>}

      {quiet && (
        <div className="an-live__quiet">
          <div>
            <p className="an-live__quiet-title">Nobody here right now.</p>
            {lastVisitLine(recent, now) && <p className="an-muted">{lastVisitLine(recent, now)}</p>}
          </div>
          {todayLine(recent) && <p className="an-faint">{todayLine(recent)}</p>}
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="an-live__cols" aria-hidden="true">
            <span>Who</span>
            <span>Where now</span>
            <span>Trail</span>
            <span>Came from</span>
            <span className="an-right">On site</span>
          </div>
          <ul className="an-live__list">
            {rows.map((e) => (
              <LiveRow key={e.id} entry={e} now={now} onOpen={onOpen} />
            ))}
          </ul>
          <p className="an-live__foot">
            Updates on its own: rows appear when someone arrives, move when they change page, fade out when they leave. People we know sort to
            the top.
          </p>
        </>
      )}
    </section>
  );
}

function LiveRow({ entry, now, onOpen }) {
  const { look, flag, flagText } = liveLook(entry, now);
  const chip = stageChip(entry.person);
  const steps = trail(entry);
  return (
    <li className={`an-liverow is-${look}${entry.leaving ? " is-leaving" : ""}`}>
      <div className="an-liverow__who">
        {entry.person ? (
          <button type="button" className="an-person" onClick={() => onOpen && onOpen(entry.person, entry)}>
            <span className="an-person__name">{entry.person.name}</span>
            {entry.person.company && <span className="an-person__co">· {entry.person.company}</span>}
            <StageChip chip={chip} />
          </button>
        ) : (
          <span className="an-someone">Someone</span>
        )}
        {entry.country && (
          <span className="an-liverow__geo">
            <Country code={entry.country} />
          </span>
        )}
        {flag && (
          <span className="an-flag">
            <span className={`an-flag__pill is-${look}`}>{flag}</span>
            {flagText}
          </span>
        )}
      </div>
      <div className="an-liverow__where">
        <PropDot prop={entry.property} />
        <span className={entry.person ? "an-strong" : ""}>{whereLabel(entry)}</span>
      </div>
      <div className="an-liverow__trail">
        {steps.map((p, i) => (
          <React.Fragment key={`${p}-${i}`}>
            {i > 0 && (
              <span aria-hidden="true" className="an-faint">
                →
              </span>
            )}
            <span className={i === steps.length - 1 ? "an-strong" : "an-muted"}>{p}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="an-liverow__src">
        <SourceCell view={sourceView(entry.source)} />
      </div>
      <span className="an-liverow__time">{clock(secondsSince(entry.landed_at || entry.since, now))}</span>
    </li>
  );
}
